import Lottie, { type LottieRef } from "lottie-react";
import { useCallback, useEffect, useRef } from "react";
import { useReducedMotion } from "../Motion/useReducedMotion";
import type { LottieAnimationProps } from "./types";

/**
 * CSS rules that override the inline SVG fill/stroke values emitted by
 * lottie-web with `currentColor`, which inherits the wrapper's `color`
 * property.
 */
const COLOR_OVERRIDE_STYLES = `
.medbillai-lottie--tinted svg [stroke]:not([stroke="none"]):not([stroke="transparent"]) {
  stroke: currentColor !important;
}
.medbillai-lottie--tinted svg [fill]:not([fill="none"]):not([fill="transparent"]) {
  fill: currentColor !important;
}`;

/**
 * Web Lottie animation renderer.
 *
 * Uses `lottie-react` (lottie-web SVG renderer). Uses CSS currentColor to tint the animation.
 */
export const LottieAnimation = ({
  source,
  width,
  height,
  playing,
  loop = true,
  color
}: LottieAnimationProps) => {
  const lottieRef: LottieRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const hold = useCallback(() => {
    const frames = lottieRef.current?.getDuration(true) ?? 0;
    lottieRef.current?.goToAndStop(loop ? 0 : Math.max(frames - 1, 0), true);
  }, [loop]);
  useEffect(() => {
    if (reducedMotion) {
      hold();
    } else if (playing ?? true) {
      lottieRef.current?.play();
    } else {
      lottieRef.current?.pause();
    }
  }, [playing, reducedMotion, hold]);
  const autoplay = playing === undefined && !reducedMotion;
  return <div className={color ? "medbillai-lottie medbillai-lottie--tinted" : "medbillai-lottie"} style={{
    width,
    height,
    ...(color ? {
      color
    } : undefined),
    lineHeight: 0
  }} data-sentry-component="LottieAnimation" data-sentry-source-file="LottieAnimation.tsx">
      {color ? <style>{COLOR_OVERRIDE_STYLES}</style> : null}
      <Lottie lottieRef={lottieRef} animationData={source} autoplay={autoplay} loop={loop} onDOMLoaded={reducedMotion ? hold : undefined} style={{
      width,
      height
    }} data-sentry-element="Lottie" data-sentry-source-file="LottieAnimation.tsx" />
    </div>;
};